import { useId, useImperativeHandle, useRef } from "react";

import {
  FieldDefinitionCurrencyType,
  FieldDefinitionFieldType,
  FieldDefinitionTextType,
  FieldImperativeHandle,
  FieldSizeType,
} from "@/components/@types";
import {
  FormattedCurrencyInput,
  Input,
  NumberInput,
  PercentInput,
} from "@/components/organisms/Field/Field.utils";
import { Values } from "@/constants/values";
import { Skeleton } from "@/components/ui/skeleton";
import { typographyStyles } from "@/components/ui/typography";

function FieldTypographyValue({
  events,
  value,
  valueOptions,
  mode,
  style,
  size,
  isMultiline,
  isExpanded,
  ref,
}: FieldDefinitionFieldType<
  FieldDefinitionTextType<any> | FieldDefinitionCurrencyType<any>
> & {
  mode?: "currency" | "number" | "percent" | "date";
  isExpanded?: boolean;
  ref?: React.Ref<FieldImperativeHandle>;
}) {
  const inputRef = useRef<HTMLInputElement | HTMLTextAreaElement>(null);

  useImperativeHandle(
    ref,
    () => ({
      setFocus: () => {
        inputRef.current?.focus({ preventScroll: true });
      },
      unsetFocus: () => {
        inputRef.current?.blur();
      },
    }),
    [],
  );

  const InputComponent =
    {
      currency: FormattedCurrencyInput,
      number: NumberInput,
      percent: PercentInput,
      date: Input,
    }[mode] || Input;

  const { currency } = (valueOptions || {}) as { currency?: string | null };
  let { placeholder } = (valueOptions || {}) as { placeholder?: string };

  const defaultPlaceholder =
    {
      currency: "$44.00",
      number: "43 or 32,771.93",
      percent: "12.5%",
      date: "Jan 1, 2025",
    }[mode] || "";

  placeholder = placeholder || defaultPlaceholder;

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    events?.onChangeField(e.target.value);
  };

  const handleBlur = () => {
    events?.onSubmitField?.();
  };

  const handleKeyDown = (
    e: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    if (e.key === "Enter") {
      // Shift+Enter inserts newline in multiline mode, Enter alone submits
      if (isMultiline && e.shiftKey) {
        // Allow default behavior (insert newline)
        return;
      }
      e.preventDefault();
      (e.target as HTMLInputElement | HTMLTextAreaElement).blur();
    }
  };

  const isDisabled = !events?.onChangeField;
  const id = useId();

  const numericValue =
    value === null || value === undefined || value === ""
      ? null
      : Number(value);
  const isZeroCurrency =
    mode === "currency" &&
    (numericValue === null || Number.isNaN(numericValue) || numericValue === 0);

  // A block of no padding and a line at the least: a line of it is its own
  // line's height, whatever draws it - read, written in one line or in many,
  // picked or not, empty or not. Left inline, it sat on a line of the page's
  // own, taller text, and was as tall as that or more, by a different amount
  // for each; empty, a textarea was no line at all.
  const baseStyle: React.CSSProperties = {
    display: "block",
    padding: 0,
    minHeight: "1lh",
    width: "100%",
    maxWidth: "100%",
    minWidth: 0,
    ...({ fieldSizing: "content" } as any),
    textOverflow: "ellipsis",
    overflow: "hidden",
    border: "none",
    background: "transparent",
    outline: "none",
    resize: "none",
    ...((typographyStyles as Record<string, React.CSSProperties>)[
      `body-${size}`
    ] as React.CSSProperties),
    ...(mode === "currency" && {
      textAlign: "right",
      justifyContent: "flex-end",
    }),
    ...(mode === "number" && {
      textAlign: "right",
      justifyContent: "flex-end",
    }),
    ...(mode === "percent" && {
      textAlign: "right",
      justifyContent: "flex-end",
    }),
    ...style,
    color: isZeroCurrency
      ? "color-mix(in oklab, var(--muted-foreground) 80%, transparent)"
      : "var(--foreground)",
    // Disabled/read-only inputs ignore ``color`` in favor of the browser
    // fill color — keep them in lockstep so zero currencies stay tertiary.
    WebkitTextFillColor: isZeroCurrency
      ? "color-mix(in oklab, var(--muted-foreground) 80%, transparent)"
      : "var(--foreground)",
  };

  // Read-only multiline cells (e.g. triage JE preview) need real ellipsis;
  // textarea text-overflow is unreliable when collapsed.
  if (isMultiline && !mode && isDisabled && !isExpanded) {
    return (
      <div
        style={{
          width: "100%",
          minWidth: 0,
          overflow: "hidden",
        }}
      >
        <span
          data-testid="field-typography-multiline"
          style={{
            display: "block",
            minHeight: "1lh",
            width: "100%",
            minWidth: 0,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            border: "none",
            background: "transparent",
            ...((typographyStyles as Record<string, React.CSSProperties>)[
              `body-${size}`
            ] as React.CSSProperties),
            ...style,
            color: "var(--foreground)",
          }}
        >
          {value ?? ""}
        </span>
      </div>
    );
  }

  // Use textarea for multiline mode (non-numeric fields only).
  // A single textarea element is always rendered (stable DOM node) to preserve focus
  // across the collapsed↔expanded transition. When collapsed, the textarea is styled
  // as a single-line field with overflow clipping. When expanded, it wraps text.
  // A textarea never draws an ellipsis, so while collapsed its words are drawn
  // by a line laid over it that does, and the textarea's own are see-through.
  // The line draws them as generated content, so the words are on the page once
  // - the textarea's - to be read or found by.
  if (isMultiline && !mode) {
    return (
      <div
        style={{
          position: "relative",
          width: "100%",
          overflow: isExpanded ? "visible" : "hidden",
        }}
      >
        {!isExpanded && (
          <span
            aria-hidden
            data-value={value ?? ""}
            style={{
              position: "absolute",
              inset: 0,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              pointerEvents: "none",
              ...((typographyStyles as Record<string, React.CSSProperties>)[
                `body-${size}`
              ] as React.CSSProperties),
              ...style,
              color: "var(--foreground)",
            }}
            className="[&::before]:[content:attr(data-value)]"
          />
        )}
        <textarea
          ref={inputRef as React.RefObject<HTMLTextAreaElement>}
          name={id}
          value={value ?? ""}
          disabled={isDisabled}
          readOnly={isDisabled}
          onChange={handleChange}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          placeholder={isExpanded && !isDisabled ? placeholder : undefined}
          data-testid="field-typography-multiline"
          rows={1}
          style={
            isExpanded
              ? {
                  ...baseStyle,
                  whiteSpace: "pre-wrap",
                  wordBreak: "break-word",
                }
              : {
                  ...baseStyle,
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  resize: "none",
                  cursor: "inherit",
                  color: "transparent",
                  WebkitTextFillColor: "transparent",
                }
          }
        />
      </div>
    );
  }

  const isFitContent =
    baseStyle.width === "fit-content" || baseStyle.width === "auto";

  return (
    <InputComponent
      ref={inputRef as React.RefObject<HTMLInputElement>}
      name={id}
      value={value ?? ""}
      disabled={isDisabled}
      readOnly={isDisabled}
      onChange={handleChange}
      onBlur={handleBlur}
      onKeyDown={handleKeyDown}
      placeholder={isDisabled ? undefined : placeholder}
      {...(mode === "currency" ? { currency } : {})}
      data-testid={`field-typography-${mode || "text"}`}
      style={{
        ...baseStyle,
        whiteSpace: "nowrap",
        ...(isFitContent
          ? ({
              fieldSizing: "content",
              maxWidth: "none",
              overflow: "visible",
              textOverflow: "clip",
            } as React.CSSProperties)
          : ({ fieldSizing: "fixed" } as React.CSSProperties)),
      }}
    />
  );
}

function FieldTypographySkeleton({
  mode,
  size,
}: {
  mode?: "currency" | "number" | "percent" | "date";
  size?: FieldSizeType;
}) {
  return (
    <div
      style={{
        ...((typographyStyles as Record<string, React.CSSProperties>)[
          `body-${size}`
        ] as React.CSSProperties),
        width: "100%",
        display: "flex",
      }}
    >
      <Skeleton
        variant="text"
        level={`body-${size}`}
        style={{
          width: "75%",
          maxWidth: Values.DEFAULT_COLUMN_WIDTH,
        }}
      />
    </div>
  );
}

export function FieldTypography({
  isLoading,
  size,
  ref,
  ...props
}: FieldDefinitionFieldType<
  FieldDefinitionTextType<any> | FieldDefinitionCurrencyType<any>
> & {
  mode?: "currency" | "number" | "percent" | "date";
  isLoading?: boolean;
  isExpanded?: boolean;
  ref?: React.Ref<FieldImperativeHandle>;
}) {
  if (isLoading) {
    return <FieldTypographySkeleton mode={props.mode} size={size} />;
  }

  return <FieldTypographyValue {...props} size={size} ref={ref} />;
}
